import {
  getChatSessionsByChatSessionIdWireMessagesInfiniteOptions,
  getChatSessionsByChatSessionIdWireMessagesInfiniteQueryKey,
} from '@onefootprint/fetch-dashboard';
import type { ChatSessionWireMessage } from '@onefootprint/request-types/dashboard';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const PAGE_SIZE = 64;

/**
 * Loads every persisted wire-message row for a chat session by walking the
 * cursor-paginated `/chat_sessions/{id}/wire_messages` endpoint. Used when resuming
 * a session so the chat panel shows full history before the websocket connects. The
 * rows are the same wire messages the websocket streams live, so the panel renders
 * resumed history and live messages through one path.
 */
export const useChatSessionMessages = (
  chatSessionId: string | undefined,
  isLive: boolean | undefined,
  // Caller gates this off until it's confirmed the session isn't running, so a
  // running session's WS `ready` history wins over the lagging Rust snapshot.
  enabled = true,
) => {
  const queryClient = useQueryClient();

  // Synchronously evict any cached pages from a prior visit before
  // `useInfiniteQuery` reads from the cache. `gcTime: 0` alone is unreliable
  // because TanStack's GC fires on a `setTimeout`, so a fast
  // `/chat/X → /ask-percy → /chat/X` round-trip can re-attach an observer
  // before the timer runs and the stale pages get served immediately.
  useState(() => {
    if (!chatSessionId) return null;
    queryClient.removeQueries({
      queryKey: getChatSessionsByChatSessionIdWireMessagesInfiniteQueryKey({
        path: { chatSessionId },
        query: { pageSize: PAGE_SIZE },
      }),
    });
    return null;
  });

  const query = useInfiniteQuery({
    ...getChatSessionsByChatSessionIdWireMessagesInfiniteOptions({
      path: { chatSessionId: chatSessionId ?? '' },
      query: { pageSize: PAGE_SIZE },
      headers: isLive === undefined ? undefined : { 'x-is-live': String(isLive) },
    }),
    enabled: enabled && !!chatSessionId && isLive !== undefined,
    initialPageParam: 0,
    getNextPageParam: lastPage => lastPage.meta.next ?? undefined,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const messages: ChatSessionWireMessage[] = query.data?.pages.flatMap(p => p.data) ?? [];

  // Gate on `isFetching` (any fetch in flight) instead of just `isLoading`
  // (only true when there's no cached data). With background refetches
  // disabled above, `isFetching` becomes a clean signal for "we're waiting
  // for fresh server data" — so ChatSession never mounts against pages
  // that may be missing recently-persisted messages.
  const isLoading = query.isFetching || query.hasNextPage === true || query.isFetchingNextPage;

  return { messages, isLoading, error: query.error };
};
